import { get, isEmpty, noop } from 'lodash-es';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { setError } from '../../reducers/error';
import { setLoader } from '../../reducers/loader';
import { API_TO_MARKET_SEGMENT_MAPPER } from '../../../constants/equity/common';

import {
	createWatchlist,
	deleteReorderWatchlist,
	deleteScripFromWatchlists,
	fetchAllRecommendedWatchlist,
	fetchAllWatchlists,
	resetWatchlistData,
	setAllRecommendedWatchlist,
	setSelectedWatchlist,
	setWatchlistError,
	setWatchlistLoader,
	updateWatchlist,
	updateWatchlistData,
	updateWatchlistItems,
	updateWatchlistStorage,
	getWatchlistGuestMarketData,
	updateScripsMarketData,
} from '../../reducers/equity/watchlist';
import {
	create_watchlist,
	delete_reorder_watchlist,
	delete_scrips_from_watchlists,
	fetch_all_recommended_watchlist,
	fetch_all_watchlists,
	update_watchlist,
	update_watchlist_items,
} from '../../../apis/equity/watchlist';
import { fetch_master_scrip_data_list } from '../../../apis/equity/guestLogin';
import { COMMON } from '../../../strings/common';

export default function* fetchWatchlistData() {
	yield takeLatest(
		fetchAllRecommendedWatchlist.toString(),
		handleFetchAllRecommendedWatchlist,
	);
	yield takeLatest(fetchAllWatchlists.toString(), handleFetchAllWatchlists);
	yield takeLatest(createWatchlist.toString(), handleCreateWatchlist);
	yield takeLatest(
		deleteReorderWatchlist.toString(),
		handleDeleteReorderWatchlist,
	);
	yield takeLatest(
		updateWatchlistItems.toString(),
		handleUpdateWatchlistItems,
	);
	yield takeLatest(updateWatchlist.toString(), handleUpdateWatchlist);
	yield takeLatest(
		deleteScripFromWatchlists.toString(),
		handleDeleteScripFromWatchlists,
	);
	yield takeLatest(
		getWatchlistGuestMarketData.toString(),
		handleWlGuestMarketData,
	);
}

function* handleFetchAllRecommendedWatchlist({ payload }) {
	const { screen } = payload;
	try {
		yield put(setLoader({ screen, isPageLoading: true }));
		yield put(setError({ screen, isFetchFailed: false, message: '' }));
		const response = yield call(fetch_all_recommended_watchlist);
		const data = get(response, 'data', []);
		yield put(setAllRecommendedWatchlist(data));
	} catch (error) {
		console.log('[Fetch Recommended Watchlist Saga]', error);
		yield put(
			setError({ screen, isFetchFailed: true, message: error.message }),
		);
	} finally {
		yield put(setLoader({ screen, isPageLoading: false }));
	}
}

function* handleFetchAllWatchlists({ payload = {} }) {
	const {
		Api,
		isGuestApiAllowed,
		sagaCallback = noop,
		isPullToRefresh = false,
		pullToRefreshCallback = noop,
		showLoader = true,
	} = payload;
	try {
		yield put(setWatchlistLoader(showLoader));
		yield put(setWatchlistError({}));
		const response = yield call(
			fetch_all_watchlists,
			Api,
			isGuestApiAllowed,
		);
		const data = get(response, 'data', {});
		yield put(resetWatchlistData(data));
		yield put(updateWatchlistStorage({ isWatchlistDataFetched: true }));
		yield call(sagaCallback, data, isPullToRefresh);
	} catch (error) {
		console.log('[Fetch Watchlist Saga]', error);
		if (isPullToRefresh) {
			const message =
				error?.message === COMMON.networkErrorMessage
					? error?.message
					: 'Unable to load data.';
			yield call(pullToRefreshCallback, message);
		} else {
			yield put(
				setWatchlistError({
					isFetchFailed: true,
					message: error.message,
				}),
			);
		}
	} finally {
		yield put(setWatchlistLoader(false));
	}
}

function* handleCreateWatchlist({ payload }) {
	const {
		Api,
		screen,
		data,
		sagaCallback = noop,
		closeLoader = noop,
		shouldUpdateSelectedStore = true,
	} = payload;
	try {
		yield put(setLoader({ screen, isButtonLoading: true }));
		yield put(setError({ screen, isUpdateFailed: false, message: '' }));
		const response = yield call(create_watchlist, Api, data);
		const newWatchlist = get(response, 'data', {});
		if (!isEmpty(newWatchlist)) {
			const newWatchlistId = Object.keys(newWatchlist)[0];
			if (shouldUpdateSelectedStore) {
				yield put(
					setSelectedWatchlist({
						id: newWatchlistId,
						...newWatchlist[newWatchlistId],
					}),
				);
			}
			yield put(updateWatchlistData(newWatchlist));
		}
		yield call(sagaCallback, response);
	} catch (error) {
		console.log('[Create Watchlist Saga]', error);
		yield call(sagaCallback, {}, true);
		yield put(
			setError({ screen, isUpdateFailed: true, message: error.message }),
		);
	} finally {
		yield call(closeLoader);
		yield put(setLoader({ screen, isButtonLoading: false }));
	}
}

function* handleDeleteReorderWatchlist({ payload }) {
	const { Api, screen, data, watchlistData, sagaCallback = noop } = payload;
	try {
		yield put(setLoader({ screen, isButtonLoading: true }));
		yield put(setError({ screen, isUpdateFailed: false, message: '' }));
		yield call(delete_reorder_watchlist, Api, data);
		yield put(resetWatchlistData(watchlistData));
		yield call(sagaCallback);
	} catch (error) {
		console.log(error);
		yield put(
			setError({ screen, isUpdateFailed: true, message: error.message }),
		);
	} finally {
		yield put(setLoader({ screen, isButtonLoading: false }));
	}
}

function* handleUpdateWatchlistItems({ payload }) {
	const {
		Api,
		screen = '',
		data,
		sagaCallback = noop,
		closeLoader = noop,
	} = payload;
	try {
		const response = yield call(update_watchlist_items, Api, data);
		yield call(sagaCallback, { data: response.data });
	} catch (error) {
		console.log('[Update Watchlist Items Saga]', error);
		yield call(sagaCallback, { isError: true });
		yield put(
			setError({ screen, isUpdateFailed: true, message: error.message }),
		);
	} finally {
		yield call(closeLoader);
		yield put(setLoader({ screen, isButtonLoading: false }));
	}
}

function* handleUpdateWatchlist({ payload }) {
	const { Api, screen, data, sagaCallback = noop } = payload;
	try {
		yield put(setLoader({ screen, isButtonLoading: true }));
		yield put(setError({ screen, isUpdateFailed: false, message: '' }));
		const response = yield call(update_watchlist, Api, data);
		yield call(sagaCallback, response);
	} catch (error) {
		console.log('[Update Watchlist Saga]', error);
		yield put(
			setError({ screen, isUpdateFailed: true, message: error.message }),
		);
	} finally {
		yield put(setLoader({ screen, isButtonLoading: false }));
	}
}

function* handleDeleteScripFromWatchlists({ payload }) {
	const {
		Api,
		screen,
		data,
		sagaCallback = noop,
		closeLoader = noop,
	} = payload;
	try {
		yield put(setLoader({ screen, isButtonLoading: true }));
		yield put(setError({ screen, isUpdateFailed: false, message: '' }));
		const response = yield call(delete_scrips_from_watchlists, Api, data);
		yield call(sagaCallback, response);
	} catch (error) {
		console.log('[Delete Scrips From Watchlists Saga]', error);
		yield call(sagaCallback, { isError: true });
		yield put(
			setError({ screen, isUpdateFailed: true, message: error.message }),
		);
	} finally {
		yield call(closeLoader);
		yield put(setLoader({ screen, isButtonLoading: false }));
	}
}

function* handleWlGuestMarketData({ payload }) {
	const {
		Api,
		scripsTokenList = [],
		sagaCallback = noop,
		isGuestOrDownTime = false,
	} = payload;
	try {
		const response = yield call(
			fetch_master_scrip_data_list,
			Api,
			scripsTokenList,
			isGuestOrDownTime,
		);
		const result = get(response, 'scrip_data', []);

		const wlMarketData = {};
		scripsTokenList?.forEach((scrip) => {
			const marketData = result[`${scrip.token}_${scrip.marketSegment}`];
			wlMarketData[
				`${scrip.token} ${
					API_TO_MARKET_SEGMENT_MAPPER[scrip.marketSegment]
				}`
			] = {
				oneDayChangePercentage:
					marketData?.net_change_percentage?.toFixed(2),
				oneDayChangeValue: marketData?.net_change_value?.toFixed(2),
				marketValue: marketData.ltp,
			};
		});

		yield put(updateScripsMarketData(wlMarketData));
	} catch (error) {
		console.log('[handleWlGuestMarketData Watchlists Saga]', error);
		yield call(sagaCallback, { isError: true });
	}
}
